import type { TJobStatus, ICaseListItem, IListCasesQuery } from '@escloud/types';
import {
    Search,
    FolderOpen,
    Loader2,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    Trash2,
    Eye,
} from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router';

import { CaseRowActions } from '@/components/cases/case-row-actions';
import { NewCaseDropdown } from '@/components/cases/new-case-dropdown';
import { Page, PageBody } from '@/components/layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useTenant } from '@/context';
import { useCases, useWorkflows } from '@/hooks';
import { useCurrentTenantDetails } from '@/hooks/use-tenants';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const;
const DEFAULT_PAGE_SIZE = 10;

const JOB_STATUS_OPTIONS = [
    { value: 'all', label: 'All Statuses' },
    { value: 'queued', label: 'Queued' },
    { value: 'processing', label: 'Processing' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
] as const;

export function CasesPage() {
    const { tenantId } = useTenant();
    const { data: tenant, isLoading: tenantLoading } = useCurrentTenantDetails();
    const { data: workflowsData } = useWorkflows();

    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [workflowFilter, setWorkflowFilter] = useState<string>('all');
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [currentPage, setCurrentPage] = useState(0);

    const queryParams = useMemo(() => {
        const params: IListCasesQuery = {
            limit: pageSize,
            offset: currentPage * pageSize,
        };
        if (searchQuery.trim()) {
            params.name = searchQuery.trim();
        }
        if (statusFilter !== 'all') {
            params.jobStatus = statusFilter as TJobStatus;
        }
        if (workflowFilter !== 'all') {
            params.workflowId = workflowFilter;
        }
        return params;
    }, [searchQuery, statusFilter, workflowFilter, pageSize, currentPage]);

    // Reset to first page when filters change
    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        setCurrentPage(0);
    };

    const handleStatusFilterChange = (value: string) => {
        setStatusFilter(value);
        setCurrentPage(0);
    };

    const handleWorkflowFilterChange = (workflowId: string) => {
        setWorkflowFilter(workflowId);
        setCurrentPage(0);
    };

    const handlePageSizeChange = (value: string) => {
        setPageSize(Number(value));
        setCurrentPage(0);
    };

    const { data: casesData, isLoading: casesLoading, error: casesError } = useCases(queryParams);

    const totalPages = casesData ? Math.ceil(casesData.total / pageSize) : 0;

    const workflowMap = useMemo(() => {
        const map = new Map<string, string>();
        workflowsData?.items.forEach((w) => map.set(w.id, w.name));
        return map;
    }, [workflowsData]);

    /**
     * Determine if the case can be opened based on latest job status.
     */
    const canOpenCase = (caseItem: ICaseListItem): boolean => {
        const status = caseItem.latestJob?.status;
        return status === 'completed';
    };

    if (tenantLoading) {
        return (
            <Page>
                <PageBody scroll="y">
                    <CasesSkeleton />
                </PageBody>
            </Page>
        );
    }

    return (
        <Page>
            <PageBody scroll="y">
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold">{tenant?.name}</h1>
                            <p className="text-muted-foreground">Manage your medical imaging cases</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" asChild>
                                <Link to={`/tenants/${tenantId}/cases/trash`}>
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Trash
                                </Link>
                            </Button>
                            <NewCaseDropdown />
                        </div>
                    </div>

                    <Card>
                        <CardHeader>
                            <CardTitle>Cases</CardTitle>
                            <CardDescription>Your medical imaging cases</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Filters */}
                            <div className="flex flex-col gap-4 sm:flex-row">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        placeholder="Search by case name..."
                                        value={searchQuery}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                        className="pl-9"
                                    />
                                </div>

                                {/* Workflow filter pills if more than 1 workflow available */}
                                {workflowsData?.items && workflowsData.items.length > 1 && (
                                    <div className="flex flex-wrap gap-2">
                                        <Badge
                                            variant={workflowFilter === 'all' ? 'default' : 'outline'}
                                            className="cursor-pointer"
                                            onClick={() => handleWorkflowFilterChange('all')}
                                        >
                                            All
                                        </Badge>
                                        {workflowsData.items.map((workflow) => (
                                            <Badge
                                                key={workflow.id}
                                                variant={
                                                    workflowFilter === workflow.id ? 'default' : 'outline'
                                                }
                                                className="cursor-pointer"
                                                onClick={() => handleWorkflowFilterChange(workflow.id)}
                                            >
                                                {workflow.shortname}
                                            </Badge>
                                        ))}
                                    </div>
                                )}

                                <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                                    <SelectTrigger className="w-full sm:w-[180px]">
                                        <SelectValue placeholder="Filter by status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {JOB_STATUS_OPTIONS.map((option) => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {/* Content */}
                            {casesLoading ? (
                                <CasesTableSkeleton />
                            ) : casesError ? (
                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <AlertCircle className="h-12 w-12 text-destructive mb-4" />
                                    <h3 className="text-lg font-medium">Failed to load cases</h3>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Please try again later
                                    </p>
                                </div>
                            ) : !casesData?.items.length ? (
                                <EmptyState
                                    hasFilters={
                                        !!searchQuery || statusFilter !== 'all' || workflowFilter !== 'all'
                                    }
                                />
                            ) : (
                                <div className="rounded-md border">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Case Name</TableHead>
                                                <TableHead>Patient</TableHead>
                                                <TableHead>Workflow</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>Created</TableHead>
                                                <TableHead className="w-24">Open</TableHead>
                                                <TableHead className="w-12"></TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {casesData.items.map((caseItem) => (
                                                <TableRow key={caseItem.id}>
                                                    <TableCell className="font-medium">
                                                        {caseItem.name}
                                                    </TableCell>
                                                    <TableCell>
                                                        <PatientInfo caseItem={caseItem} />
                                                    </TableCell>
                                                    <TableCell>
                                                        {workflowMap.get(caseItem.workflowId) ||
                                                            caseItem.workflowId}
                                                    </TableCell>
                                                    <TableCell>
                                                        <JobStatusBadge job={caseItem.latestJob} />
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {formatDate(caseItem.createdAt)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {canOpenCase(caseItem) ? (
                                                            <Button variant="ghost" size="sm" asChild>
                                                                <Link
                                                                    to={`/tenants/${tenantId}/cases/${caseItem.id}/viewer`}
                                                                >
                                                                    <Eye className="mr-1 h-4 w-4" />
                                                                    Open
                                                                </Link>
                                                            </Button>
                                                        ) : (
                                                            <Button variant="ghost" size="sm" disabled>
                                                                <Eye className="mr-1 h-4 w-4" />
                                                                Open
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <CaseRowActions caseItem={caseItem} />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}

                            {/* Pagination controls */}
                            {casesData && casesData.total > 0 && (
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="text-sm text-muted-foreground">
                                        Showing {currentPage * pageSize + 1}-
                                        {Math.min((currentPage + 1) * pageSize, casesData.total)} of{' '}
                                        {casesData.total} cases
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-muted-foreground">
                                                Rows per page:
                                            </span>
                                            <Select
                                                value={pageSize.toString()}
                                                onValueChange={handlePageSizeChange}
                                            >
                                                <SelectTrigger className="w-[70px]">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {PAGE_SIZE_OPTIONS.map((size) => (
                                                        <SelectItem key={size} value={size.toString()}>
                                                            {size}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                onClick={() => setCurrentPage((p) => p - 1)}
                                                disabled={currentPage === 0}
                                            >
                                                <ChevronLeft className="h-4 w-4" />
                                            </Button>
                                            <span className="min-w-[100px] text-center text-sm">
                                                Page {currentPage + 1} of {totalPages}
                                            </span>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                onClick={() => setCurrentPage((p) => p + 1)}
                                                disabled={currentPage >= totalPages - 1}
                                            >
                                                <ChevronRight className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </PageBody>
        </Page>
    );
}

function PatientInfo({ caseItem }: { caseItem: ICaseListItem }) {
    const parts = [caseItem.lastname, caseItem.firstname].filter(Boolean);
    if (parts.length === 0) {
        return <span className="text-muted-foreground">—</span>;
    }
    return <span>{parts.join(', ')}</span>;
}

function JobStatusBadge({ job }: { job: ICaseListItem['latestJob'] }) {
    if (!job) {
        return (
            <Badge variant="outline" className="text-muted-foreground">
                No jobs
            </Badge>
        );
    }

    switch (job.status) {
        case 'queued':
            return (
                <Badge
                    variant="secondary"
                    className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                >
                    Queued
                </Badge>
            );
        case 'processing':
            return <ProcessingBadge createdAt={job.createdAt} />;
        case 'completed':
            return (
                <Badge
                    variant="secondary"
                    className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                >
                    Completed
                </Badge>
            );
        case 'failed':
            return <Badge variant="destructive">Failed</Badge>;
        default:
            return <Badge variant="outline">{job.status}</Badge>;
    }
}

function ProcessingBadge({ createdAt }: { createdAt: string }) {
    const [, setTick] = useState(0);

    useEffect(() => {
        const getIntervalMs = () => {
            const start = new Date(createdAt);
            const now = new Date();
            const elapsedSeconds = Math.floor((now.getTime() - start.getTime()) / 1000);

            // First minute: update every second
            if (elapsedSeconds < 60) return 1000;
            // 1-10 minutes: update every 10 seconds
            if (elapsedSeconds < 600) return 10000;
            // 10+ minutes: update every minute
            return 60000;
        };

        let timeoutId: ReturnType<typeof setTimeout>;

        const scheduleUpdate = () => {
            const intervalMs = getIntervalMs();
            timeoutId = setTimeout(() => {
                setTick((t) => t + 1);
                scheduleUpdate();
            }, intervalMs);
        };

        scheduleUpdate();

        return () => clearTimeout(timeoutId);
    }, [createdAt]);

    return (
        <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            Processing {formatElapsedTime(createdAt)}
        </Badge>
    );
}

function EmptyState({ hasFilters }: { hasFilters: boolean }) {
    return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
            <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">{hasFilters ? 'No matching cases' : 'No cases yet'}</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                {hasFilters
                    ? 'Try adjusting your search or filter criteria'
                    : 'Create your first case to get started with medical imaging analysis'}
            </p>
        </div>
    );
}

function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

function formatElapsedTime(dateString: string): string {
    const start = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();

    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
        const remainingMinutes = minutes % 60;
        return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    }
    if (minutes > 0) {
        const remainingSeconds = seconds % 60;
        return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    }
    return `${seconds}s`;
}

function CasesSkeleton() {
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="mt-2 h-4 w-64" />
                </div>
                <Skeleton className="h-10 w-28" />
            </div>
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-4 w-48" />
                </CardHeader>
                <CardContent>
                    <CasesTableSkeleton />
                </CardContent>
            </Card>
        </div>
    );
}

function CasesTableSkeleton() {
    return (
        <div className="space-y-4">
            <div className="flex gap-4">
                <Skeleton className="h-10 flex-1" />
                <Skeleton className="h-10 w-[180px]" />
            </div>
            <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                ))}
            </div>
        </div>
    );
}
